import { AlertCircle, CheckCircle, File, FileText, Image, Upload, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';

export interface FileUploadItem {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

interface FileUploadProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  maxFiles?: number;
  files: FileUploadItem[];
  onChange: (files: FileUploadItem[]) => void;
  onUpload?: (files: FileUploadItem[]) => Promise<void>;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  hint?: string;
  showProgress?: boolean;
  autoUpload?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept,
  multiple = false,
  maxSize = 10,
  maxFiles = 5,
  files,
  onChange,
  onUpload,
  required = false,
  error,
  disabled = false,
  className = '',
  hint,
  showProgress = false,
  autoUpload = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error: showError } = useToast();

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      files.forEach(fileItem => {
        if (fileItem.file.type.startsWith('image/')) {
          URL.revokeObjectURL(URL.createObjectURL(fileItem.file));
        }
      });
    };
  }, []);

  const validateFile = useCallback(
    (file: File): { valid: boolean; error?: string } => {
      // Check file size
      if (file.size > maxSize * 1024 * 1024) {
        return { valid: false, error: `File size exceeds ${maxSize}MB limit` };
      }

      // Check file type if accept is specified
      if (accept) {
        const acceptedTypes = accept.split(',').map(type => type.trim());
        const fileName = file.name.toLowerCase();
        const _fileExtension = '.' + fileName.split('.').pop();

        const isValidType = acceptedTypes.some(type => {
          // If type is a file extension (starts with .)
          if (type.startsWith('.')) {
            return fileName.endsWith(type.toLowerCase());
          }
          // If type includes wildcard (like image/*)
          if (type.includes('*')) {
            const baseType = type.split('/')[0];
            return file.type.startsWith(baseType);
          }
          // If type is a MIME type
          return file.type === type;
        });

        if (!isValidType) {
          return { valid: false, error: `File type not supported. Accepted: ${accept}` };
        }
      }

      return { valid: true };
    },
    [accept, maxSize]
  );

  const handleFileSelect = useCallback(
    async (selectedFiles: FileList | null) => {
      if (!selectedFiles || disabled) return;

      const newFileItems: FileUploadItem[] = [];
      const errors: string[] = [];

      Array.from(selectedFiles).forEach(file => {
        // Check file count
        if (files.length + newFileItems.length >= maxFiles) {
          errors.push(`Maximum ${maxFiles} files allowed`);
          return;
        }

        const validation = validateFile(file);
        if (!validation.valid) {
          errors.push(`${file.name}: ${validation.error}`);
          return;
        }

        newFileItems.push({
          file,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          progress: 0,
          status: 'pending',
        });
      });

      // Show validation errors
      if (errors.length > 0) {
        showError('File validation failed', errors.join(', '));
        return;
      }

      const updatedFiles = multiple ? [...files, ...newFileItems] : newFileItems.slice(0, 1);
      onChange(updatedFiles);

      // Auto-upload if enabled
      if (autoUpload && onUpload && newFileItems.length > 0) {
        try {
          setIsUploading(true);
          await onUpload(newFileItems);
          success('Files uploaded successfully');
        } catch (err) {
          showError('Upload failed', err instanceof Error ? err.message : 'Unknown error');
        } finally {
          setIsUploading(false);
        }
      }
    },
    [
      files,
      maxFiles,
      validateFile,
      multiple,
      onChange,
      autoUpload,
      onUpload,
      disabled,
      showError,
      success,
    ]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!disabled) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = useCallback(
    (fileId: string) => {
      const fileItem = files.find(f => f.id === fileId);
      if (fileItem?.file.type.startsWith('image/')) {
        URL.revokeObjectURL(URL.createObjectURL(fileItem.file));
      }

      const newFiles = files.filter(f => f.id !== fileId);
      onChange(newFiles);
    },
    [files, onChange]
  );

  const retryUpload = useCallback(
    async (fileId: string) => {
      if (!onUpload) return;

      const fileItem = files.find(f => f.id === fileId);
      if (!fileItem) return;

      const updatedFiles = files.map(f =>
        f.id === fileId ? { ...f, status: 'pending' as const, error: undefined } : f
      );
      onChange(updatedFiles);

      try {
        setIsUploading(true);
        await onUpload([fileItem]);
        success('File re-uploaded successfully');
      } catch (err) {
        showError('Retry failed', err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsUploading(false);
      }
    },
    [files, onUpload, onChange, success, showError]
  );

  const getFileIcon = useCallback((file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-4 w-4" />;
    } else if (file.type.includes('pdf') || file.type.includes('document')) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  }, []);

  const getStatusIcon = useCallback((fileItem: FileUploadItem) => {
    switch (fileItem.status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'uploading':
        return (
          <div className="h-4 w-4 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
        );
      default:
        return null;
    }
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragOver
            ? 'border-red-400 bg-red-50'
            : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-gray-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={e => handleFileSelect(e.target.files)}
          className="hidden"
          disabled={disabled}
        />

        <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-sm text-gray-600 mb-2">
          <span className="font-medium text-red-500">Click to upload</span> or drag and drop
        </p>
        <p className="text-xs text-gray-500">
          {accept ? `Accepted formats: ${accept}` : 'All file types accepted'}
          {maxSize && ` • Max size: ${maxSize}MB`}
          {multiple && maxFiles && ` • Max files: ${maxFiles}`}
        </p>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Files ({files.length}/{maxFiles}):
          </p>
          {files.map(fileItem => (
            <div
              key={fileItem.id}
              className={`p-3 rounded-lg border ${
                fileItem.status === 'error'
                  ? 'bg-red-50 border-red-200'
                  : fileItem.status === 'completed'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1">
                  {getFileIcon(fileItem.file)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileItem.file.name}
                    </p>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span>{formatFileSize(fileItem.file.size)}</span>
                      {getStatusIcon(fileItem)}
                      {fileItem.status === 'uploading' && showProgress && (
                        <span>{Math.round(fileItem.progress)}%</span>
                      )}
                    </div>
                    {fileItem.error && (
                      <p className="text-xs text-red-600 mt-1">{fileItem.error}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  {fileItem.status === 'error' && onUpload && (
                    <button
                      type="button"
                      onClick={() => retryUpload(fileItem.id)}
                      className="p-1 text-orange-500 hover:text-orange-700 transition-colors"
                      title="Retry upload"
                    >
                      <Upload className="h-4 w-4" />
                    </button>
                  )}
                  {!disabled && fileItem.status !== 'uploading' && (
                    <button
                      type="button"
                      onClick={() => removeFile(fileItem.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove file"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              {showProgress && fileItem.status === 'uploading' && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${fileItem.progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {hint && !error && <p className="mt-1 text-sm text-gray-500">{hint}</p>}
    </div>
  );
};

export default FileUpload;
